import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const severityConfig = {
  danger: {
    icon: DeleteOutlineRoundedIcon,
    color: '#EF4444',
    bg: 'rgba(239, 68, 68, 0.08)',
    confirmColor: 'error',
  },
  warning: {
    icon: WarningAmberRoundedIcon,
    color: '#F59E0B',
    bg: 'rgba(245, 158, 11, 0.08)',
    confirmColor: 'warning',
  },
  info: {
    icon: InfoOutlinedIcon,
    color: '#3B82F6',
    bg: 'rgba(59, 130, 246, 0.08)',
    confirmColor: 'primary',
  },
};

export default function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'danger',
  loading = false,
  // Optional free-text capture, for the confirms that used to be a
  // window.prompt (e.g. "reason for cancelling dispatch"). Purely additive:
  // when `input` is undefined this renders exactly as before, so the 25
  // existing consumers are unaffected. Shape: { label, placeholder, multiline }.
  input = null,
  inputValue = '',
  onInputChange = null,
}) {
  const config = severityConfig[severity] || severityConfig.danger;
  const Icon = config.icon;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ pt: 4, pb: 1, textAlign: 'center' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            backgroundColor: config.bg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}
        >
          <Icon sx={{ fontSize: 28, color: config.color }} />
        </Box>
        <DialogTitle
          sx={{
            pt: 0,
            pb: 1,
            px: 3,
            fontSize: '1.125rem',
            fontWeight: 700,
            textAlign: 'center',
          }}
        >
          {title}
        </DialogTitle>
      </Box>
      <DialogContent sx={{ textAlign: 'center', px: 4, pb: 2 }}>
        {/* pre-line keeps paragraph breaks in messages that carry \n\n. Messages
            without newlines render exactly as before. */}
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}>
          {message}
        </Typography>
        {input && (
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label={input.label}
            placeholder={input.placeholder}
            multiline={!!input.multiline}
            minRows={input.multiline ? 2 : undefined}
            value={inputValue}
            onChange={(e) => onInputChange?.(e.target.value)}
            disabled={loading}
            sx={{ mt: 2, textAlign: 'left' }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1, justifyContent: 'center' }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          variant="outlined"
          sx={{
            minWidth: 100,
            borderRadius: '10px',
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'text.secondary',
              backgroundColor: 'rgba(0,0,0,0.03)',
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color={config.confirmColor}
          sx={{
            minWidth: 100,
            borderRadius: '10px',
            boxShadow: 'none',
            fontWeight: 600,
            '&:hover': {
              boxShadow: `0 4px 12px ${config.bg}`,
            },
          }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
